import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import ButtonLink from "./ButtonLink";
import {
  DASHBOARD_SIGNUP_URL,
  IOS_APP_DESTINATION_URL,
  META_QUEST_STORE_URL,
  WEB_VIEWER_URL,
} from "../data/platform-links";
import "./SiteHeader.css";

const navItems = [
  { label: "For Educators", href: "/educators" },
  { label: "Creators", href: "/creators" },
  { label: "Content", href: "/content" },
  { label: "Pricing", href: "/pricing" },
  { label: "Stories", href: "/stories" },
  { label: "Contact", href: "/contact" },
  { label: "Login", href: "https://dashboard.10k.science" },
];

const getItLinks = [
  {
    label: "Get it on App Store",
    href: IOS_APP_DESTINATION_URL,
  },
  {
    label: "Try the full platform",
    href: DASHBOARD_SIGNUP_URL,
  },
  {
    label: "Download VR for Meta Quest",
    href: META_QUEST_STORE_URL,
  },
  {
    label: "See it now on the web",
    href: WEB_VIEWER_URL,
  },
];

const ExternalOrInternalLink = ({ href, children, className, onClick }) => {
  if (/^https?:/i.test(href)) {
    return (
      <a
        className={className}
        href={href}
        onClick={onClick}
        rel="noreferrer"
      >
        {children}
      </a>
    );
  }

  return (
    <NavLink className={className} onClick={onClick} to={href}>
      {children}
    </NavLink>
  );
};

export default function SiteHeader() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [mobileFolderOpen, setMobileFolderOpen] = useState(false);
  const [getItOpen, setGetItOpen] = useState(false);
  const [headerHidden, setHeaderHidden] = useState(false);
  const location = useLocation();
  const dropdownRef = useRef(null);
  const previousScroll = useRef(0);

  useEffect(() => {
    setMenuOpen(false);
    setMobileFolderOpen(false);
    setGetItOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.classList.toggle("menu-open", menuOpen);
    return () => document.body.classList.remove("menu-open");
  }, [menuOpen]);

  useEffect(() => {
    const handleScroll = () => {
      const currentScroll = Math.max(window.scrollY, 0);
      const movingDown = currentScroll > previousScroll.current;
      setHeaderHidden(
        !menuOpen && movingDown && currentScroll > window.innerHeight * 0.15,
      );
      previousScroll.current = currentScroll;
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [menuOpen]);

  useEffect(() => {
    const handlePointerDown = (event) => {
      if (
        getItOpen &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setGetItOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setGetItOpen(false);
        setMenuOpen(false);
      }
    };

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [getItOpen]);

  return (
    <header
      className={`site-header${headerHidden ? " site-header--hidden" : ""}`}
    >
      <div className="site-header__inner content-width">
        <Link aria-label="10k Science home" className="site-logo" to="/">
          <img
            alt="10k Science"
            src="/assets/original/brand/10k-science-logo-white.png"
          />
        </Link>

        <div className="site-header__desktop">
          <nav aria-label="Main navigation" className="site-nav">
            <div
              className="site-nav__folder"
              onMouseEnter={() => setGetItOpen(true)}
              onMouseLeave={() => setGetItOpen(false)}
              ref={dropdownRef}
            >
              <button
                aria-controls="get-it-dropdown"
                aria-expanded={getItOpen}
                className="site-nav__folder-trigger"
                onClick={() => setGetItOpen((current) => !current)}
                type="button"
              >
                Get it now
              </button>
              <div
                className="site-nav__dropdown"
                hidden={!getItOpen}
                id="get-it-dropdown"
              >
                {getItLinks.map((item) => (
                  <ExternalOrInternalLink
                    href={item.href}
                    key={item.label}
                  >
                    {item.label}
                  </ExternalOrInternalLink>
                ))}
              </div>
            </div>

            {navItems.map((item) => (
              <ExternalOrInternalLink href={item.href} key={item.label}>
                {item.label}
              </ExternalOrInternalLink>
            ))}
          </nav>

          <ButtonLink
            href={WEB_VIEWER_URL}
            size="small"
            target="_blank"
          >
            Live web preview
          </ButtonLink>
        </div>

        <button
          aria-controls="mobile-menu"
          aria-expanded={menuOpen}
          aria-label={menuOpen ? "Close Menu" : "Open Menu"}
          className={`menu-toggle${menuOpen ? " is-open" : ""}`}
          onClick={() => {
            setMenuOpen((current) => !current);
            if (menuOpen) setMobileFolderOpen(false);
          }}
          type="button"
        >
          <span />
          <span />
        </button>
      </div>

      <div
        aria-hidden={!menuOpen}
        className={`mobile-menu${menuOpen ? " is-open" : ""}`}
        id="mobile-menu"
      >
        <div
          className={`mobile-menu__track${
            mobileFolderOpen ? " is-folder-open" : ""
          }`}
        >
          <nav
            aria-hidden={mobileFolderOpen}
            aria-label="Mobile navigation"
            className="mobile-menu__panel"
          >
            <div className="mobile-menu__links">
              <button
                aria-label="Folder: Get it now"
                className="mobile-menu__folder-trigger"
                onClick={() => setMobileFolderOpen(true)}
                type="button"
              >
                <span>Get it now</span>
                <span
                  aria-hidden="true"
                  className="mobile-menu__folder-icon"
                >
                  <svg
                    fill="none"
                    stroke="currentColor"
                    strokeLinecap="square"
                    strokeLinejoin="miter"
                    strokeWidth="0.5"
                    viewBox="0 0 22 22"
                  >
                    <path d="M18 7L11 14L4 7" />
                  </svg>
                </span>
              </button>
              {navItems.map((item) => (
                <ExternalOrInternalLink
                  href={item.href}
                  key={item.label}
                  onClick={() => setMenuOpen(false)}
                >
                  {item.label}
                </ExternalOrInternalLink>
              ))}
            </div>
            <ButtonLink
              href={WEB_VIEWER_URL}
              onClick={() => setMenuOpen(false)}
              target="_blank"
            >
              Live web preview
            </ButtonLink>
          </nav>

          <nav
            aria-hidden={!mobileFolderOpen}
            aria-label="Get it now"
            className="mobile-menu__panel mobile-menu__panel--folder"
          >
            <div className="mobile-menu__links mobile-menu__links--folder">
              <button
                aria-label="Back"
                className="mobile-menu__back"
                onClick={() => setMobileFolderOpen(false)}
                type="button"
              >
                <span
                  aria-hidden="true"
                  className="mobile-menu__back-icon"
                >
                  <svg
                    fill="none"
                    stroke="currentColor"
                    strokeLinecap="square"
                    strokeLinejoin="miter"
                    strokeWidth="0.5"
                    viewBox="0 0 22 22"
                  >
                    <path d="M18 7L11 14L4 7" />
                  </svg>
                </span>
                <span>Back</span>
              </button>
              {getItLinks.map((item) => (
                <ExternalOrInternalLink
                  href={item.href}
                  key={item.label}
                  onClick={() => setMenuOpen(false)}
                >
                  {item.label}
                </ExternalOrInternalLink>
              ))}
            </div>
          </nav>
        </div>
      </div>
    </header>
  );
}
